import { useState, useCallback, useMemo } from 'react';
import type { LayoutChangeEvent, NativeScrollEvent } from 'react-native';
import { View, StyleSheet } from 'react-native';
import { Flex, ColorValues, useScreenSize } from '@banhala/ui-kit';
import { HorizontalDragScrollView } from '../HorizontalDragScrollView';
import type { IconListProps, IconItem } from './types';
import type { AnyObject } from '../../../../types';
import { ShortcutIcon } from './components/ShortcutIcon';
import { isNil } from '../../../../utils';
import { useLogClickScrollableIconList } from './hooks/useLogClickScrollableIconList';

interface LinePagerIndicatorProps {
  scrollPosition: number;
  contentWidth: number;
  containerWidth: number;
  itemCount: number;
}

/**
 * Android LinePagerIndicator와 동일한 동작 구현
 * - 트랙이 화면 중앙에 배치
 * - indicatorSize = 32dp
 * - 트랙 너비 = indicatorSize * totalItems / visibleItems
 * - 인디케이터는 스크롤 진행률에 따라 0 ~ (trackWidth - indicatorSize) 범위에서 이동
 */
const LinePagerIndicator = ({
  scrollPosition,
  contentWidth,
  containerWidth,
  itemCount,
}: LinePagerIndicatorProps) => {
  const indicatorSize = 32;

  const itemWidth = contentWidth / itemCount;
  const visibleItems = containerWidth / itemWidth;

  const trackWidth = (indicatorSize * itemCount) / visibleItems;

  const scrollableWidth = contentWidth - containerWidth;
  const progress =
    scrollableWidth > 0 ? Math.min(scrollPosition / scrollableWidth, 1) : 0;

  const indicatorPosition = progress * (trackWidth - indicatorSize);

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      padding={{ vertical: 11 }}
    >
      <View
        style={[
          indicatorStyles.track,
          { width: trackWidth, backgroundColor: ColorValues.gray30 },
        ]}
      >
        <View
          style={[
            indicatorStyles.indicator,
            {
              width: indicatorSize,
              backgroundColor: ColorValues.gray70,
              marginLeft: indicatorPosition,
            },
          ]}
        />
      </View>
    </Flex>
  );
};

const indicatorStyles = StyleSheet.create({
  track: {
    height: 2,
    borderRadius: 1,
  },
  indicator: {
    height: 2,
    borderRadius: 1,
  },
});

type IconData = { item: IconItem; logging: AnyObject };

type IconRowItem = { type: 'icon'; data: IconData } | { type: 'empty' };

const MIN_COLUMN_WIDTH = 64;

export const ScrollableIconList = ({ entity, body }: IconListProps) => {
  const logClickIcon = useLogClickScrollableIconList();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const { width: screenWidth } = useScreenSize();

  const handleScroll = useCallback(
    (event: { nativeEvent: NativeScrollEvent }) => {
      setScrollPosition(event.nativeEvent.contentOffset.x);
    },
    [],
  );

  const handleContentSizeChange = useCallback((width: number) => {
    setContentWidth(width);
  }, []);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const description = body?.item ?? {
    row_size: 2,
    has_indicator: false,
    icon_size: 'MEDIUM' as const,
  };

  const rowSize = description.row_size || 2;
  const iconSize = description.icon_size === 'SMALL' ? 'small' : 'medium';
  const verticalGap = iconSize === 'small' ? 8 : 4;
  const hasIndicator = description.has_indicator;
  const canScroll = contentWidth > containerWidth;
  const showIndicator = hasIndicator && canScroll;

  // 아이콘 너비 계산: 화면에 보이는 열 개수를 기준으로 산정
  // contentContainerStyle의 paddingHorizontal(8*2) 공제
  const columnCount = Math.ceil((entity ?? []).length / rowSize);

  const HORIZONTAL_PADDING = 8;
  const itemWidth = useMemo(() => {
    if (rowSize === 1) {
      return MIN_COLUMN_WIDTH;
    }
    return Math.max(
      MIN_COLUMN_WIDTH,
      (screenWidth - HORIZONTAL_PADDING * (columnCount - 1)) / columnCount,
    );
  }, [columnCount, screenWidth, rowSize]);

  const rowsData = useMemo(() => {
    if (isNil(entity)) return [];
    const rows: IconRowItem[][] = [];
    for (let row = 0; row < rowSize; row++) {
      const rowData: IconRowItem[] = [];
      for (let i = row; i < entity.length; i += rowSize) {
        rowData.push({ type: 'icon', data: entity[i] });
      }
      // 행 길이가 짧으면 열 정렬을 맞추기 위해 empty로 채움
      while (rowData.length < columnCount) {
        rowData.push({ type: 'empty' });
      }
      rows.push(rowData);
    }
    return rows;
  }, [columnCount, entity, rowSize]);

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <Flex onLayout={handleLayout}>
      <HorizontalDragScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        scrollEventThrottle={16}
        contentContainerStyle={{ paddingHorizontal: 8 }}
      >
        <Flex gap={verticalGap}>
          {rowsData.map((row, rowIndex) => (
            <Flex.Row key={rowIndex} gap={4}>
              {row.map((iconData, colIndex) => {
                if (iconData.type === 'icon') {
                  return (
                    <ShortcutIcon
                      key={colIndex}
                      item={iconData.data.item}
                      size={iconSize}
                      width={itemWidth}
                      logging={iconData.data.logging}
                      onLogClick={logClickIcon}
                      testID={`scrollable_icon_list?name=${iconData.data.item.name}`}
                    />
                  );
                }
                return (
                  <Flex key={`iconData.type-${colIndex}`} width={itemWidth} />
                );
              })}
            </Flex.Row>
          ))}
        </Flex>
      </HorizontalDragScrollView>
      {showIndicator ? (
        <LinePagerIndicator
          scrollPosition={scrollPosition}
          contentWidth={contentWidth}
          containerWidth={containerWidth}
          itemCount={columnCount}
        />
      ) : (
        <Flex height={12} />
      )}
    </Flex>
  );
};
